import { useState, useEffect } from "react";
import { X, Banknote, AlertTriangle, Loader2 } from "lucide-react";
import axios from "axios";
import { POST_BULK_DEBT_PAYMENT_URL } from "../../../features/UrlHelper";
import "./BulkDebtPaymentModal.scss";

function formatAmount(val) {
  return `${Number(val).toFixed(2).replace(".", ",")} €`;
}

function BulkDebtPaymentModal({ debtors, onClose, onSuccess }) {
  const totalDebt = debtors.reduce((sum, debtor) => {
    const debt = Math.abs(parseFloat(debtor?.actual_saldo) || 0);
    return sum + debt;
  }, 0);

  const [totalAmount, setTotalAmount] = useState(() => {
    return totalDebt > 0 ? totalDebt.toFixed(2) : "";
  });
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Reset total amount when debtors change
  useEffect(() => {
    setTotalAmount(totalDebt > 0 ? totalDebt.toFixed(2) : "");
    setError("");
  }, [totalDebt]);

  async function handleSubmit(e) {
    e.preventDefault();

    // Validation
    if (!totalAmount || parseFloat(totalAmount) <= 0) {
      setError("Suma musí byť väčšia ako 0.");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const debtorIds = debtors.map((d) => d.id);
      await axios.post(
        POST_BULK_DEBT_PAYMENT_URL,
        { debtor_ids: debtorIds }
      );
      onSuccess();
    } catch (err) {
      setError(err?.response?.data?.message || "Nastala chyba pri hromadnom zaevidovaní platby.");
    } finally {
      setIsLoading(false);
    }
  }

  const handleOverlayMouseDown = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className="bpayment__overlay" onMouseDown={handleOverlayMouseDown}>
      <div className="bpayment">
        <div className="bpayment__header">
          <h2 className="bpayment__title">Hromadné vyplácanie dlhov</h2>
          <button className="bpayment__close" onClick={onClose} aria-label="Zavrieť">
            <X size={18} />
          </button>
        </div>

        <div className="bpayment__body">
          {/* Summary of selected debtors */}
          <div className="bpayment__debtors-list">
            <h3 className="bpayment__debtors-title">Vybratí dlžníci ({debtors.length})</h3>
            <div className="bpayment__debtors-items">
              {debtors.map((debtor) => {
                const debtAmount = Math.abs(parseFloat(debtor?.actual_saldo) || 0);
                return (
                  <div key={debtor.id} className="bpayment__debtor-item">
                    <div className="bpayment__debtor-name">
                      {debtor.firstname} {debtor.lastname}
                    </div>
                    <div className="bpayment__debtor-debt">
                      {formatAmount(debtAmount)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Total amount info */}
          <div className="bpayment__info">
            <div className="bpayment__info-row">
              <span className="bpayment__info-label">Celková suma na úhradu</span>
              <span className="bpayment__info-value bpayment__info-value--total">
                {formatAmount(totalDebt)}
              </span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="bpayment__form">
            <label className="bpayment__label" htmlFor="bpayment-amount">
              Suma na úhradu (€)
            </label>
            <input
              id="bpayment-amount"
              type="text"
              className={`bpayment__input bpayment__input--disabled${error ? " bpayment__input--error" : ""}`}
              value={totalAmount}
              disabled
              readOnly
              title="V hromadnom režime sa platí presná celková suma. Suma sa nemôže upravovať."
            />
            <div className="bpayment__info-note">
              <span className="bpayment__note-icon">ⓘ</span>
              <span className="bpayment__note-text">
                V hromadnom režime sa platí presná suma všetkých vybraných dlhov. Suma sa nemôže upravovať.
              </span>
            </div>

            {error && (
              <div className="bpayment__error">
                <AlertTriangle size={14} />
                <span>{error}</span>
              </div>
            )}

            <div className="bpayment__actions">
              <button
                type="button"
                className="bpayment__btn bpayment__btn--cancel"
                onClick={onClose}
                disabled={isLoading}
              >
                Zrušiť
              </button>
              <button
                type="submit"
                className="bpayment__btn bpayment__btn--submit"
                disabled={isLoading}
              >
                {isLoading
                  ? <Loader2 size={16} className="bpayment__spinner" />
                  : <Banknote size={16} />}
                <span>{isLoading ? "Spracovávam..." : "Zaevidovať hromadnú platbu"}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default BulkDebtPaymentModal;
